import { apiClient } from "./client";
import type {
  Event,
  EventCreateRequest,
  EventGroup,
  TournamentResultRequest,
} from "../types/swagger";
import { DartsEvent, ParticipantPaymentFee } from "../types";
import { mockEventGroups } from "../mocks/fixtures/eventGroups";
import { mockEvents } from "../mocks/fixtures/events";

export type { ParticipantPaymentFee };

export type { EventCreateRequest, EventGroup, TournamentResultRequest };

const USE_MOCK = process.env.REACT_APP_USE_MOCK === "true";

let mockEventsStore: Event[] = mockEvents.map((event) => ({ ...event })) as Event[];

const findMockEventGroup = (eventGroupId?: string | number) => {
  if (eventGroupId == null) return mockEventGroups[0];
  return (
    mockEventGroups.find((group) => group.id === String(eventGroupId)) ??
    mockEventGroups[0]
  );
};

/** UTC ISO 8601 datetime string → JST "yyyy / mm / dd hh:mm" フォーマットに変換 */
function formatEventDateTime(isoString: string): string {
  if (!isoString) return "";
  try {
    const d = new Date(isoString);
    if (Number.isNaN(d.getTime())) return isoString;
    // UTCにJST offset（+9時間）を加算
    const jst = new Date(d.getTime() + 9 * 60 * 60 * 1000);
    const yyyy = jst.getUTCFullYear();
    const mm = String(jst.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(jst.getUTCDate()).padStart(2, "0");
    const hh = String(jst.getUTCHours()).padStart(2, "0");
    const min = String(jst.getUTCMinutes()).padStart(2, "0");
    return `${yyyy} / ${mm} / ${dd} ${hh}:${min}`;
  } catch {
    return isoString;
  }
}

export function toDE(e: Event): DartsEvent {
  const eventGroupName = e.event_group?.name?.trim() ?? "";
  const toUiRankingPoints = (points?: { rank: number; point: number }[]) =>
    points?.map((p) => ({ rank: String(p.rank), point: p.point }));

  return {
    id: e.id,
    eventGroupId: e.event_group?.id,
    tournament: eventGroupName || e.title,
    title: eventGroupName ? e.title : "",
    datetime: e.datetime,
    date: formatEventDateTime(e.datetime),
    location: e.place,
    participants: e.participants ?? [],
    participantCount: e.participants?.length ?? 0,
    format: e.singles_flag ? "シングルス" : "ダブルス",
    matchFormat: e.match_format,
    hasPoints: e.singles_point_flag || e.doubles_point_flag,
    isExciting: e.singles_ex_point_flag,
    fee: e.participation_fee,
    notes: e.other,
    isPrimary: e.singles_point_flag && e.singles_ex_point_flag,
    status: e.event_ended_flg
      ? "finished"
      : e.tournament_started_flg
        ? "in_progress"
        : "scheduled",
    entryStatus: e.joined_flg ? "entered" : "recruiting",
    singlesFlag: e.singles_flag,
    singlesPointFlag: e.singles_point_flag,
    singlesRankingPoints: toUiRankingPoints(e.singles_ranking_points),
    doublesFlag: e.doubles_flag,
    doublesPointFlag: e.doubles_point_flag,
    doublesRankingPoints: toUiRankingPoints(e.doubles_ranking_points),
    paymentFlg: e.payment_flg,
  };
}

export type UpdateEventRequest = EventCreateRequest;

export type { TournamentResultRequest as ResultRequest };

export const eventsApi = {
  /** イベント一覧取得 GET /events */
  getAll: async (): Promise<Event[]> => {
    if (USE_MOCK) {
      return mockEventsStore;
    }
    const { data } = await apiClient.get<Event[]>("/events");
    return data;
  },

  /** イベントグループ一覧取得 GET /event_groups */
  getEventGroups: async (): Promise<EventGroup[]> => {
    if (USE_MOCK) {
      return mockEventGroups;
    }
    const { data } = await apiClient.get<EventGroup[]>("/event_groups");
    return data;
  },

  /** イベント作成（管理者のみ）POST /event */
  create: async (body: EventCreateRequest): Promise<void> => {
    if (USE_MOCK) {
      const eventGroup = findMockEventGroup(body.event_groups_id);
      const newEvent: Event = {
        id: crypto.randomUUID(),
        event_group: {
          id: String(body.event_groups_id),
          name: eventGroup?.name ?? "",
        },
        joined_flg: false,
        title: body.title,
        datetime: body.datetime,
        place: body.place,
        singles_flag: body.singles_flag,
        singles_point_flag: body.singles_point_flag,
        singles_ex_point_flag: body.singles_ex_point_flag,
        doubles_flag: body.doubles_flag,
        doubles_point_flag: body.doubles_point_flag,
        match_format: body.match_format ?? "",
        participation_fee: body.participation_fee,
        other: body.other,
        participation_start_datetime: new Date().toISOString(),
        tournament_created_flg: false,
        tournament_started_flg: false,
        event_ended_flg: false,
        payment_flg: false,
        singles_ranking_points: [],
        doubles_ranking_points: [],
        participants: [],
      };
      mockEventsStore.push(newEvent);
      return;
    }
    await apiClient.post("/event", body);
  },

  /** イベント修正（管理者のみ）PUT /event/{event_id} */
  update: async (id: string, body: UpdateEventRequest): Promise<void> => {
    if (USE_MOCK) {
      const index = mockEventsStore.findIndex((event) => String(event.id) === id);
      if (index === -1) return;

      const eventGroup = body.event_groups_id
        ? findMockEventGroup(body.event_groups_id)
        : undefined;

      mockEventsStore[index] = {
        ...mockEventsStore[index],
        event_group: eventGroup
          ? { id: eventGroup.id, name: eventGroup.name }
          : mockEventsStore[index].event_group,
        title: body.title,
        datetime: body.datetime,
        place: body.place,
        singles_flag: body.singles_flag,
        singles_point_flag: body.singles_point_flag,
        singles_ex_point_flag: body.singles_ex_point_flag,
        doubles_flag: body.doubles_flag,
        doubles_point_flag: body.doubles_point_flag,
        match_format: body.match_format,
        participation_fee: body.participation_fee,
        other: body.other,
      };
      return;
    }
    await apiClient.put(`/event/${id}`, body);
  },

  /** イベント削除（管理者のみ）DELETE /event/{event_id} */
  remove: async (id: string): Promise<void> => {
    if (USE_MOCK) {
      mockEventsStore = mockEventsStore.filter((event) => String(event.id) !== id);
      return;
    }
    await apiClient.delete(`/event/${id}`);
  },

  /** イベント参加 POST /event/{event_id}/entry */
  entry: async (eventId: string): Promise<void> => {
    if (USE_MOCK) {
      const index = mockEventsStore.findIndex(
        (event) => String(event.id) === eventId,
      );
      if (index !== -1) {
        mockEventsStore[index] = {
          ...mockEventsStore[index],
          joined_flg: true,
        };
      }
      return;
    }
    await apiClient.post(`/event/${eventId}/entry`);
  },

  /** イベント参加キャンセル DELETE /event/{event_id}/entry */
  cancelEntry: async (eventId: string): Promise<void> => {
    if (USE_MOCK) {
      const index = mockEventsStore.findIndex(
        (event) => String(event.id) === eventId,
      );
      if (index !== -1) {
        mockEventsStore[index] = {
          ...mockEventsStore[index],
          joined_flg: false,
        };
      }
      return;
    }
    await apiClient.delete(`/event/${eventId}/entry`);
  },

  /** 試合開始（管理者のみ）POST /event/{event_id}/start */
  start: async (eventId: string): Promise<void> => {
    if (USE_MOCK) {
      const index = mockEventsStore.findIndex(
        (event) => String(event.id) === eventId,
      );
      if (index !== -1) {
        mockEventsStore[index] = {
          ...mockEventsStore[index],
          tournament_started_flg: true,
        };
      }
      return;
    }
    await apiClient.post(`/event/${eventId}/start`);
  },

  /** 試合結果登録 POST /event/{event_id}/tournament/result */
  registerResult: async (eventId: string, body: TournamentResultRequest): Promise<void> => {
    if (USE_MOCK) {
      void eventId;
      void body;
      return;
    }
    await apiClient.post(`/event/${eventId}/tournament/result`, body);
  },

  /** 試合終了（管理者のみ）POST /event/{event_id}/end */
  end: async (eventId: string): Promise<void> => {
    if (USE_MOCK) {
      const index = mockEventsStore.findIndex(
        (event) => String(event.id) === eventId,
      );
      if (index !== -1) {
        mockEventsStore[index] = {
          ...mockEventsStore[index],
          tournament_started_flg: false,
          event_ended_flg: true,
          tournament_created_flg: true,
        };
      }
      return;
    }
    await apiClient.post(`/event/${eventId}/end`);
  },

  /** 参加者支払い料金取得（管理者のみ）GET /event/{event_id}/participants/payment_fees */
  getParticipantPaymentFees: async (eventId: string): Promise<ParticipantPaymentFee[]> => {
    if (USE_MOCK) {
      const event = mockEventsStore.find((e) => String(e.id) === eventId);
      if (!event || !event.participants || event.participants.length === 0) return [];
      return event.participants.map((name) => ({
        participant_name: name,
        payment_fee: 2000,
      }));
    }
    const { data } = await apiClient.get<ParticipantPaymentFee[]>(`/event/${eventId}/participants/payment_fees`);
    return data;
  },
};
